import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Plus,
  Package,
  PackageCheck,
  PackageX,
  Search,
  ArrowUpRight,
  MoreHorizontal,
} from 'lucide-react';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { finishedGoodsService } from '@/services/finishedGoods';
import { inventoryService } from '@/services/inventory';
import type { FinishedGood, FinishedGoodStatus } from '@/types/finished-goods';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import { FinishedGoodDetailSheet } from './FinishedGoodDetailSheet';
import { FinishedGoodInwardSheet } from './FinishedGoodInwardSheet';

type StatusFilter = 'all' | FinishedGoodStatus;

const STATUS_LOWER: Record<FinishedGoodStatus, string> = {
  AVAILABLE: 'available',
  RESERVED: 'reserved',
  DISPATCHED: 'dispatched',
  RETURNED: 'returned',
  GOOD: 'restocked',
  BAD: 'rejected',
  WRITE_OFF: 'archived',
};

export function FinishedGoodsPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<FinishedGood | null>(null);
  const [inwardOpen, setInwardOpen] = useState(false);
  const [density, setDensity] = useState<TableDensity>('compact');
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['finished-goods', { q: debounced }],
    queryFn: () => finishedGoodsService.list({ pageSize: 200, barcode: debounced || undefined }),
  });

  // Items + warehouses for nested display lookup (BE row may or may not include them).
  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { forFGNames: true }],
    queryFn: () => inventoryService.list({ pageSize: 200 }),
    staleTime: 60_000,
  });

  const itemById = useMemo(() => {
    const m = new Map<string, NonNullable<typeof itemsQuery.data>['items'][number]>();
    for (const it of itemsQuery.data?.items ?? []) m.set(it.id, it);
    return m;
  }, [itemsQuery.data]);

  const allRows = useMemo(() => data?.rows ?? [], [data]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: allRows.length,
      AVAILABLE: 0,
      RESERVED: 0,
      DISPATCHED: 0,
      RETURNED: 0,
      GOOD: 0,
      BAD: 0,
      WRITE_OFF: 0,
    };
    for (const u of allRows) c[u.status] += 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return allRows;
    return allRows.filter((u) => u.status === filter);
  }, [allRows, filter]);

  const summary = useMemo(
    () => ({
      total: counts.all,
      available: counts.AVAILABLE,
      dispatched: counts.DISPATCHED,
    }),
    [counts],
  );

  const tabs: StatusTabOption[] = [
    { value: 'all',        label: 'All',        count: counts.all },
    { value: 'AVAILABLE',  label: 'Available',  count: counts.AVAILABLE },
    { value: 'RESERVED',   label: 'Reserved',   count: counts.RESERVED },
    { value: 'DISPATCHED', label: 'Dispatched', count: counts.DISPATCHED },
    { value: 'RETURNED',   label: 'Returned',   count: counts.RETURNED },
    { value: 'GOOD',       label: 'Good (QC)',  count: counts.GOOD },
    { value: 'BAD',        label: 'Bad (QC)',   count: counts.BAD },
    { value: 'WRITE_OFF',  label: 'Write-off',  count: counts.WRITE_OFF },
  ];

  const columns: Column<FinishedGood>[] = [
    {
      key: 'barcode',
      header: 'Barcode',
      width: '220px',
      cell: (r) => <span className="font-mono text-[11.5px]">{r.barcode}</span>,
    },
    {
      key: 'sku',
      header: 'SKU · Item',
      cell: (r) => {
        const item = r.item ?? itemById.get(r.itemId);
        return (
          <div className="space-y-0.5">
            <div className="font-medium text-foreground">{item?.name ?? '—'}</div>
            <div className="font-mono text-[10.5px] text-muted-foreground">{item?.sku ?? r.itemId.slice(0, 8)}</div>
          </div>
        );
      },
    },
    {
      key: 'warehouse',
      header: 'Warehouse',
      width: '160px',
      cell: (r) => (
        <span className="text-[12.5px] text-foreground">
          {r.warehouse?.name ?? r.warehouseId.slice(0, 8)}
        </span>
      ),
    },
    {
      key: 'received',
      header: 'Received at',
      width: '180px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {fmt.dateTime(r.createdAt)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '130px',
      cell: (r) => <StatusBadge status={STATUS_LOWER[r.status]} />,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '90px',
      hoverReveal: true,
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => setSelected(r)}
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSelected(r)}>Open detail</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <ListPageHeader
          title="Finished Goods"
          count={counts.all}
          actions={
            <Button size="sm" onClick={() => setInwardOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              Receive batch
            </Button>
          }
        />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiCard label="Total units" value={fmt.number(summary.total)} tone="primary" icon={Package} />
          <KpiCard label="Available" value={fmt.number(summary.available)} tone="success" icon={PackageCheck} />
          <KpiCard label="Dispatched" value={fmt.number(summary.dispatched)} tone="info" icon={PackageX} />
        </div>

        <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by barcode…"
              className="h-8 pl-8 text-[13px] font-mono"
            />
          </div>
          <DensityToggle value={density} onChange={setDensity} />
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            onRowClick={setSelected}
            density={density}
            emptyTitle={
              filter === 'all' ? 'No finished goods yet' : `No ${STATUS_LOWER[filter]} units`
            }
            emptyDescription={
              filter === 'all'
                ? 'Receive your first batch — each barcode becomes one tracked unit.'
                : 'Try a different status filter.'
            }
            emptyAction={
              filter === 'all' ? (
                <Button size="sm" onClick={() => setInwardOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  Receive batch
                </Button>
              ) : null
            }
          />
        )}

        <FinishedGoodDetailSheet
          item={selected}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
        />

        <FinishedGoodInwardSheet open={inwardOpen} onOpenChange={setInwardOpen} />
      </div>
    </TooltipProvider>
  );
}
